import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { generatePath, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { InviteModal } from '../../../app/components/account/invite-modal';
import { UserPreferences } from '../../../app/components/account/user-preferences-modal';
import ReloadPrompt from '../../../app/components/reload-prompt/reload-prompt';
import { Role } from '../../../app/constants/auth';
import { Paths, QueryParams } from '../../../app/constants/paths';
import { AuthorizationContext } from '../../../app/context/authorization-context';
import { useTenant } from '../../../app/context/tenant-context';
import UserPreferencesContextProvider from '../../../app/context/user-preferences-context';
import { useTheme } from '../../../app/hooks/accounts';
import logo from '../../../assets/cr-logo.svg';
import { CreateTabOptions, useTabManager } from '../../context/tab-manager-context';
import { useGetTenantConfig } from '../../hooks/tenants';

import { Invite, Logo, Menu, Nav, NavMenuItem, Profile, StyledSidebar, TooltipText } from './style';

import {
  Dropdown,
  Icon,
  Modal,
  ModalContents,
  ModalOpenButton,
  Tooltip,
} from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { ViewType } from '@controlrooms/models';

const UserDropdownContainer = styled.div`
  position: relative;
`;

/**
 * Custom menu item component that creates tabs when LayoutV3 is enabled
 */
const SidebarMenuItem: React.FC<{
  to: string;
  'data-testid'?: string;
  end?: boolean;
  children: React.ReactNode;
  tabOptions: CreateTabOptions;
}> = ({ to, 'data-testid': testId, end, children, tabOptions }) => {
  const { createTab, openSavedView } = useTabManager();

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      if (tabOptions.viewId) {
        // If viewId provided, use openSavedView (creates if not found, switches if found)
        await openSavedView(tabOptions.viewId);
      } else {
        // Always create a new tab of the specified type
        await createTab(tabOptions);
      }
    },
    [createTab, openSavedView, tabOptions],
  );

  return (
    <NavMenuItem
      to={to}
      data-testid={testId}
      end={end}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
    >
      {children}
    </NavMenuItem>
  );
};

const Sidebar = () => {
  const { logout, user } = useAuth0();

  const { data: tenantConfig } = useGetTenantConfig();
  const [searchParams, setSearchParams] = useSearchParams();

  const [, themeName] = useTheme();
  const { tenant } = useTenant();
  const [isUserSelectOpen, setIsUserSelectOpen] = useState(false);
  const [isUserPrefsModalOpen, setIsUserPrefsModalOpen] = useState(false);
  const { canUserReadAlert, hasRole } = useContext(AuthorizationContext);

  const profileInitial = useMemo(() => {
    const name = user?.name || '';
    const initial =
      name
        .match(/(\b\S)?/g)
        ?.join('')
        ?.match(/(^\S|\S$)?/g)
        ?.join('')
        .toUpperCase() || 'U';
    return initial;
  }, [user?.name]);

  return (
    <StyledSidebar className="app-sidebar">
      <Logo className="logo" data-testid="cr-logo">
        <img src={logo} />
      </Logo>
      <Nav>
        <Menu>
          {hasRole(Role.ALERT_DASHBOARD_V2) && (
            <SidebarMenuItem
              to={generatePath(Paths.ALERT_BOARD, { tenantId: tenant.toString() })}
              data-testid="alert-board"
              tabOptions={{ type: 'ALERT_BOARD' }}
            >
              <Icon name={ICONS.Alert} />
              <TooltipText>Alert Board</TooltipText>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem
            end={true}
            to={generatePath(Paths.VIEWS, { tenantId: tenant.toString() })}
            data-testid="views"
            tabOptions={{ type: 'VIEWS', viewType: ViewType.MONITOR }}
          >
            <Icon name={ICONS.Views} />
            <TooltipText>Views</TooltipText>
          </SidebarMenuItem>

          {hasRole(Role.ALERT_MANAGER_V2) && (
            <SidebarMenuItem
              to={generatePath(Paths.MANAGE_ALERTS, { tenantId: tenant.toString() })}
              data-testid="alert-manager"
              tabOptions={{ type: 'ALERT_MANAGER' }}
            >
              <Icon name={ICONS.Gear} />
              <TooltipText>Alert Manager</TooltipText>
            </SidebarMenuItem>
          )}
          {canUserReadAlert && (
            <SidebarMenuItem
              to={generatePath(Paths.MANAGE_LIMITS, { tenantId: tenant.toString() })}
              data-testid="limit-manager"
              tabOptions={{ type: 'LIMIT_MANAGER' }}
            >
              <Icon name={ICONS.Limits} />
              <TooltipText>Limit Manager</TooltipText>
            </SidebarMenuItem>
          )}
          {(hasRole(Role.MLOPS) || hasRole(Role.MLOPS_VIEWONLY) || hasRole(Role.TEAMS_ADMIN)) && (
            <SidebarMenuItem
              to={generatePath(Paths.MANAGE, { tenantId: tenant.toString() })}
              data-testid="manage"
              tabOptions={{ type: 'MANAGE', subPage: 'monitors' }}
            >
              <Icon name={ICONS.Manage} />
              <TooltipText>Manage</TooltipText>
            </SidebarMenuItem>
          )}
        </Menu>
      </Nav>
      <Nav style={{ flex: 'none' }}>
        <ReloadPrompt />
        {tenantConfig?.showInvite && (
          <Modal open={Boolean(searchParams.get(QueryParams.INVITE))}>
            <ModalOpenButton>
              <Tooltip
                label="Invite a Colleague"
                place="right-end"
                theme={themeName.toLowerCase()}
                delay="100"
              >
                <Invite
                  buttonSize="large"
                  data-testid="invite-a-colleague"
                  style={{
                    backgroundColor: 'transparent',
                    width: '100%',
                    justifyContent: 'space-evenly',
                  }}
                  buttonType="icon"
                  iconName="export"
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.append(QueryParams.INVITE, 'true');
                    params.append(QueryParams.ROLE, Role.OBSERVER);
                    return setSearchParams(params);
                  }}
                />
              </Tooltip>
            </ModalOpenButton>
            <ModalContents
              closeButtonCallback={() => {
                const _params = new URLSearchParams(searchParams);
                _params.delete(QueryParams.INVITE);
                _params.delete(QueryParams.ROLE);
                setSearchParams(_params);
              }}
              title="Invite"
            >
              <InviteModal
                sandbox={false}
                onSubmit={() => {
                  const _params = new URLSearchParams(searchParams);
                  _params.delete(QueryParams.INVITE);
                  _params.delete(QueryParams.ROLE);
                  setSearchParams(_params);
                }}
              />
            </ModalContents>
          </Modal>
        )}

        <UserDropdownContainer>
          <Dropdown.Root isOpen={isUserSelectOpen} onClose={() => setIsUserSelectOpen(false)}>
            <Profile
              data-testid="user"
              buttonSize="small"
              buttonType="text"
              onClick={(evt) => {
                evt.stopPropagation();
                setIsUserSelectOpen((prevState) => !prevState);
              }}
            >
              {profileInitial}
            </Profile>
            {isUserSelectOpen && (
              <Dropdown.Options open={isUserSelectOpen} vertical="top" position="right">
                <Dropdown.Item
                  data-testid="user-preferences"
                  onClick={() => {
                    setIsUserPrefsModalOpen(true);
                    setIsUserSelectOpen(false);
                  }}
                >
                  User Preferences
                </Dropdown.Item>
                <Dropdown.Item
                  data-testid="log-out"
                  onClick={() => {
                    setIsUserSelectOpen(false);
                    sessionStorage.clear();
                    logout({ logoutParams: { returnTo: window.location.origin } });
                  }}
                >
                  Log Out
                </Dropdown.Item>
              </Dropdown.Options>
            )}
          </Dropdown.Root>
        </UserDropdownContainer>

        <Modal open={isUserPrefsModalOpen} onClose={() => setIsUserPrefsModalOpen(false)}>
          <ModalContents title="User Preferences" closeButtonText="Close">
            <UserPreferencesContextProvider>
              <UserPreferences />
            </UserPreferencesContextProvider>
          </ModalContents>
        </Modal>
      </Nav>
    </StyledSidebar>
  );
};

export default Sidebar;
